/**
 * Détection de l'heure de vente jamais lue sur les pièces.
 *
 * Le champ « Date et heure de la vente » est un `datetime-local` unique fabriqué
 * à partir de DEUX clés OCR (`date_vente` + `heure_vente`, cf. `PAIRED_FIELDS`).
 * Quand l'OCR n'a pas su lire l'heure, `composeDateTimeLocal` complète la date
 * avec `00:00` faute de mieux — et rien, à l'écran, ne distingue ce bouche-trou
 * d'une heure réellement extraite. L'agence valide donc « minuit » sans le
 * savoir et l'heure part fausse à l'ANTS.
 *
 * C'est une panne SILENCIEUSE, plus embêtante que le cas FK-371-EZ (16/09) : là,
 * l'heure hors bornes (« 19h75 » lu sur la mention manuscrite d'une CG barrée)
 * faisait au moins échouer l'INSERT bruyamment, donc l'agence appelait. Ici,
 * personne ne sait.
 *
 * Le prédicat vit dans son propre module, et non dans `FieldEditList.tsx`,
 * parce que la config Vitest du frontend tourne en environnement `node` et ne
 * collecte que `src/**\/*.test.ts` : un helper importé depuis un `.tsx` qui tire
 * MUI ne serait pas testable.
 */
export interface HourNotReadInput {
  /** Le champ rend-il bien un couple date+heure (`datetime-local`) ? */
  isDateTimePair: boolean;
  /** Valeur courante de la moitié « date » (override agence, sinon OCR). */
  currentValue: string;
  /** Valeur courante de la moitié « heure » (override agence, sinon OCR). */
  pairedCurrentValue: string;
  /** Confiance OCR de la moitié « heure ». `0` = non lue (ou champ absent). */
  pairedConfidence?: number;
}

/**
 * Vrai quand le `00:00` affiché est un défaut d'affichage et non une heure lue,
 * donc quand il faut le signaler à l'agence.
 *
 * Les trois conditions sont nécessaires :
 *  - une DATE est présente, sinon le champ est vide et c'est déjà signalé
 *    ailleurs (`isUnrecognized` + validation bloquante de `VerifyInfoScreen`) ;
 *  - la confiance de l'heure est nulle, c'est-à-dire qu'aucune pièce ne l'a
 *    donnée (`buildVerifyGroups` retombe sur `0` quand la clé est absente) ;
 *  - l'agence n'a pas encore saisi d'heure, sinon la mention n'a plus lieu
 *    d'être — y compris si elle a délibérément saisi minuit.
 */
export const isHourNotRead = ({
  isDateTimePair,
  currentValue,
  pairedCurrentValue,
  pairedConfidence,
}: HourNotReadInput): boolean =>
  isDateTimePair && Boolean(currentValue) && (pairedConfidence ?? 0) === 0 && !pairedCurrentValue;
